// Global Instructions Rule Applied!

import { API_ROUTES, buildFullUrl, getBackendBaseUrl } from '../../../core/utils/endpoints.ts'

const BACKEND_BASE_URL = getBackendBaseUrl()

export interface CashFlowsResponse {
  data: Record<string, unknown>[]
  error?: string
}

/**
 * Fetches Cash Flows data from the report endpoint.
 * @param fundIds - Optional list of fund UUIDs. If empty, returns all funds.
 * @param datetime - Optional ISO date string (YYYY-MM-DD) or datetime. Uses end of day for date-only.
 * @param accessToken - User's access token from Redux store
 */
export const fetchCashFlows = async (
  fundIds: string[] | null | undefined,
  datetime: string | null | undefined,
  accessToken: string | null = null
): Promise<CashFlowsResponse> => {
  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }

    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`
    }

    const body: {
      fund_ids?: string[]
      datetime?: string
    } = {}
    if (fundIds && fundIds.length > 0) {
      body.fund_ids = fundIds
    }
    if (datetime) {
      body.datetime = datetime
    }

    const response = await fetch(
      buildFullUrl(BACKEND_BASE_URL, API_ROUTES.REPORTING.CASH_FLOWS, {}, {}),
      {
        method: API_ROUTES.REPORTING.CASH_FLOWS.method,
        headers,
        body: JSON.stringify(body)
      }
    )

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Authentication failed - please log in again')
      }
      throw new Error(`Failed to fetch cash flows: ${response.status} ${response.statusText}`)
    }

    const json: unknown = await response.json()
    const parsed = json as { data?: Record<string, unknown>[] }

    return {
      data: Array.isArray(parsed.data) ? parsed.data : []
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Error fetching cash flows:', error)
    }
    return {
      data: [],
      error: error instanceof Error ? error.message : 'An unexpected error occurred'
    }
  }
}
